// src/hooks/useForms.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import formsApi from '../api/forms';

// Hook for fetching form categories
export const useFormCategories = () => {
  return useQuery({
    queryKey: ['form-categories'],
    queryFn: formsApi.getFormCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook for fetching recent form submissions
export const useRecentFormSubmissions = (limit = 10) => {
  return useQuery({
    queryKey: ['recent-form-submissions', limit],
    queryFn: () => formsApi.getRecentSubmissions(limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Hook for fetching forms by category
export const useFormsByCategory = (categoryId, params = {}) => {
  return useQuery({
    queryKey: ['forms-by-category', categoryId, params],
    queryFn: () => formsApi.getFormsByCategory(categoryId, params),
    enabled: !!categoryId && categoryId !== 'all',
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for fetching all form submissions with filtering
export const useFormSubmissions = (params = {}) => {
  return useQuery({
    queryKey: ['form-submissions', params],
    queryFn: () => formsApi.getAllSubmissions(params),
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
  });
};

// Hook for fetching a specific form template
export const useFormTemplate = (formId) => {
  return useQuery({
    queryKey: ['form-template', formId],
    queryFn: () => formsApi.getFormTemplate(formId),
    enabled: !!formId,
    staleTime: 10 * 60 * 1000, // Templates don't change often
  });
};

// Hook for fetching a specific form submission
export const useFormSubmission = (submissionId) => {
  return useQuery({
    queryKey: ['form-submission', submissionId],
    queryFn: () => formsApi.getFormSubmission(submissionId),
    enabled: !!submissionId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for searching forms
export const useFormSearch = (query, options = {}) => {
  return useQuery({
    queryKey: ['form-search', query, options],
    queryFn: () => formsApi.searchForms(query, options),
    enabled: !!query && query.length >= 2,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for form statistics
export const useFormStatistics = (dateRange = '30d') => {
  return useQuery({
    queryKey: ['form-statistics', dateRange],
    queryFn: () => formsApi.getFormStatistics(dateRange),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Mutation hooks for form operations

// Hook for creating a form template
export const useCreateFormTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: formsApi.createFormTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['form-categories'] });
      queryClient.invalidateQueries({ queryKey: ['forms-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['form-statistics'] });
      toast.success('Form template created successfully');
    },
    onError: (error) => {
      console.error('Error creating form template:', error);
      toast.error('Failed to create form template');
    },
  });
};

// Hook for updating a form template
export const useUpdateFormTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ formId, formData }) => formsApi.updateFormTemplate(formId, formData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['form-template', variables.formId] });
      queryClient.invalidateQueries({ queryKey: ['form-categories'] });
      queryClient.invalidateQueries({ queryKey: ['forms-by-category'] });
      toast.success('Form template updated successfully');
    },
    onError: (error) => {
      console.error('Error updating form template:', error);
      toast.error('Failed to update form template');
    },
  });
};

// Hook for deleting a form template
export const useDeleteFormTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: formsApi.deleteFormTemplate,
    onSuccess: (data, formId) => {
      queryClient.invalidateQueries({ queryKey: ['form-categories'] });
      queryClient.invalidateQueries({ queryKey: ['forms-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['form-statistics'] });
      queryClient.removeQueries({ queryKey: ['form-template', formId] });
      toast.success('Form template deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting form template:', error);
      toast.error('Failed to delete form template');
    },
  });
};

// Hook for bulk operations on forms
export const useBulkFormOperations = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ operation, formIds, data }) => {
      // This would need to be implemented in the API
      const promises = formIds.map(formId => {
        switch (operation) {
          case 'delete':
            return formsApi.deleteFormTemplate(formId);
          case 'update':
            return formsApi.updateFormTemplate(formId, data);
          default:
            throw new Error(`Unknown bulk operation: ${operation}`);
        }
      });
      
      return Promise.all(promises);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['form-categories'] });
      queryClient.invalidateQueries({ queryKey: ['forms-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['form-statistics'] });
      
      // Remove individual form queries for deleted forms
      if (variables.operation === 'delete') {
        variables.formIds.forEach(formId => {
          queryClient.removeQueries({ queryKey: ['form-template', formId] });
        });
      }
      
      toast.success(`Bulk ${variables.operation} operation completed successfully`);
    },
    onError: (error) => {
      console.error('Error performing bulk operation:', error);
      toast.error('Failed to complete bulk operation');
    },
  });
};

export default {
  useFormCategories,
  useRecentFormSubmissions,
  useFormsByCategory,
  useFormSubmissions,
  useFormTemplate,
  useFormSubmission,
  useFormSearch,
  useFormStatistics,
  useCreateFormTemplate,
  useUpdateFormTemplate,
  useDeleteFormTemplate,
  useBulkFormOperations,
};